import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/lib/api-client"
import type {
  Pagination,
  Post,
  PostCreateInput,
  PostsData,
  PostsQuery,
  PostUpdateInput,
} from "@/lib/types"

export const postKeys = {
  all: ["posts"] as const,
  lists: () => ["posts", "list"] as const,
  list: (filters: PostsQuery) => ["posts", "list", filters] as const,
  detail: (id: number) => ["posts", "detail", id] as const,
}

function toSearchParams(filters: PostsQuery) {
  const params = new URLSearchParams()
  if (filters.page) params.set("page", String(filters.page))
  if (filters.limit) params.set("limit", String(filters.limit))
  if (filters.search) params.set("search", filters.search)
  if (filters.categoryId) params.set("categoryId", String(filters.categoryId))
  if (filters.authorId) params.set("authorId", String(filters.authorId))
  if (filters.tagId) params.set("tagId", String(filters.tagId))
  if (filters.status && filters.status !== "all") params.set("status", filters.status)
  if (filters.sort) params.set("sort", filters.sort)
  return params.toString()
}

export function usePosts(filters: PostsQuery = {}, options: { enabled?: boolean; staleTime?: number } = {}) {
  return useQuery<PostsData>({
    queryKey: postKeys.list(filters),
    // Spread so unspecified options keep the client defaults (an explicit undefined would override them).
    ...options,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const query = toSearchParams(filters)
      // The API returns { data, pagination }; the UI works with { posts, pagination }.
      const res = await apiClient.get<{ data: Post[]; pagination: Pagination }>(
        query ? `/posts?${query}` : "/posts",
      )
      return { posts: res.data.data ?? [], pagination: res.data.pagination }
    },
  })
}

export function usePost(id: number) {
  return useQuery<Post>({
    queryKey: postKeys.detail(id),
    enabled: Number.isFinite(id) && id > 0,
    queryFn: async () => {
      const res = await apiClient.get<Post>(`/posts/${id}`)
      if (!res.data) throw new Error("Post not found")
      return res.data
    },
  })
}

export function useCreatePost() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: PostCreateInput) => (await apiClient.post<Post>("/posts", data)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: postKeys.all }),
  })
}

export function useUpdatePost(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: PostUpdateInput) => (await apiClient.put<Post>(`/posts/${id}`, data)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: postKeys.all }),
  })
}

export function useDeletePost() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/posts/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: postKeys.all }),
  })
}
